import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import { createElement, useState } from 'react'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import DashboardIcon from '@mui/icons-material/Dashboard'
import SettingsIcon from '@mui/icons-material/Settings'
import {
  MenuItemLink, MenuProps, ResourceDefinition, useGetOne,
  usePermissions, useResourceDefinitions, useSidebarState, useTranslate,
} from 'react-admin'
import { DashboardStats } from '../types/apiRequests'
import { Dot } from '../components/Icons'
import useAutoLogout from '../hooks/useAutoLogout'
import SubMenu from './SubMenu'
import { border, themeColor } from './themes'
import { SCOPES } from '../constants/scopes'

interface ResourceOptions {
  label?: string
  submenu?: string
}

interface SubmenuGroup extends Record<string, unknown> {
  name: string
  menuKey: string
  resources: string[]
  submenu: ResourceDefinition[]
}

type MenuItem = ResourceDefinition | SubmenuGroup

const getOptions = (r: ResourceDefinition): ResourceOptions =>
  (r.options ?? {}) as ResourceOptions

const Menu = ({ dense = false }: MenuProps): JSX.Element => {
  const resourcesDefinitions = useResourceDefinitions()
  // eslint-disable-next-line security/detect-object-injection
  const resources = Object.keys(resourcesDefinitions).map(name => resourcesDefinitions[name])
  const [open] = useSidebarState()
  const [state, setState] = useState({ menuSettings: false, menuAdmin: false, menuBeheer: false, menuMonitoring: false })
  const translate = useTranslate()
  const { permissions } = usePermissions<{ scopes: string[] }>()
  const hasScope = (scope: string) => permissions?.scopes?.includes(scope) ?? false

  const { data: dashboardStats } = useGetOne<DashboardStats & { id: string }>(
    'dashboard_stats',
    { id: '1' },
  )
  const pendingCount = (dashboardStats?.pending_access_requests ?? 0) + (dashboardStats?.pending_platform_requests ?? 0)

  useAutoLogout()

  const handleToggle = (menu: MenuName) => {
    // eslint-disable-next-line security/detect-object-injection
    setState(stateItem => ({ ...stateItem, [menu]: !stateItem[menu] }))
  }

  const getMenuItems = (object: ResourceDefinition[]): MenuItem[] => {
    let values: MenuItem[] = object.filter(resource => resource.icon && !getOptions(resource).submenu)
    const submenuItems = object.filter(resource => resource.icon && getOptions(resource).submenu)

    const groups: Record<string, ResourceDefinition[]> = {}
    for (const item of submenuItems) {
      const group = getOptions(item).submenu ?? ''
      // eslint-disable-next-line security/detect-object-injection
      if (!groups[group]) groups[group] = []
      // eslint-disable-next-line security/detect-object-injection
      groups[group].push(item)
    }

    for (const [group, items] of Object.entries(groups)) {
      values = [
        ...values,
        {
          name: `submenu.${group}`,
          menuKey: `menu${group.charAt(0).toUpperCase()}${group.slice(1)}`,
          resources: items.map(prop => prop.name),
          submenu: [...items]
        }
      ]
    }

    return values
  }

  return (
    <StyledBox mt={1} className={classes.root}>
      <div className={classes.spacer}>&nbsp;</div>
      <StyledMenuItemLink
        className={classes.item}
        dense={dense}
        primaryText={translate('dashboard.title')}
        sidebarIsOpen={!open}
        to="/"
        leftIcon={<DashboardIcon />}
      />
      <StyledMenuItemLink
        className={classes.item}
        dense={dense}
        primaryText={translate('menu.costExplorer')}
        sidebarIsOpen={!open}
        to="/cost-explorer"
        leftIcon={<AttachMoneyIcon />}
      />
      {getMenuItems(resources).map(resource => {
        if ('submenu' in resource) {
          return (
            <SubMenu
              key={resource.name}
              handleToggle={() => handleToggle(resource.menuKey as MenuName)}
              icon={<SettingsIcon sx={{ fontSize: 20 }} />}
              isOpen={state[resource.menuKey as MenuName]}
              sidebarIsOpen={!open}
              name={resource.name}
              dense={dense}
            >
              {resource.submenu.map(item => (
                <StyledMenuItemLink
                  className={classes.link}
                  classes={classes}
                  dense={dense}
                  key={item.name}
                  leftIcon={<Dot />}
                  primaryText={translate(getOptions(item).label ?? item.name)}
                  sidebarIsOpen={!open}
                  to={`/${item.name}`}
                />
              ))}
              {resource.name === 'submenu.beheer' && hasScope(SCOPES.ORGANIZATION_WRITE) && (
                <StyledMenuItemLink
                  className={classes.link}
                  classes={classes}
                  dense={dense}
                  key="portal-settings"
                  leftIcon={<Dot />}
                  primaryText={translate('portalSettings.menuTitle')}
                  sidebarIsOpen={!open}
                  to="/portal-settings"
                />
              )}
            </SubMenu>
          )
        }
        const label = translate(getOptions(resource).label ?? resource.name)
        const showBadge = resource.name === 'requests' && pendingCount > 0
        return (
          <StyledMenuItemLink
            className={classes.item}
            dense={dense}
            key={resource.name}
            primaryText={
              showBadge
                ? <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {label}
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 18,
                      height: 18,
                      borderRadius: '9px',
                      bgcolor: 'rgba(194,66,66,1)',
                      color: '#fff',
                      fontSize: 11,
                      fontWeight: 700,
                      lineHeight: 1,
                      px: 0.5,
                    }}
                  >
                    {pendingCount}
                  </Box>
                </Box>
                : label
            }
            sidebarIsOpen={!open}
            to={`/${resource.name}`}
            leftIcon={createElement(resource.icon)}
          />
        )
      })}
    </StyledBox>
  )
}

type MenuName = 'menuSettings' | 'menuAdmin' | 'menuBeheer' | 'menuMonitoring'

const PREFIX = 'xxllncMenu'

const classes = {
  root: `${PREFIX}-root`,
  link: `${PREFIX}-link`,
  branding: `${PREFIX}-branding`,
  logo: `${PREFIX}-logo`,
  lab: `${PREFIX}-lab`,
  active: `${PREFIX}-active`,
  icon: `${PREFIX}-icon`,
  item: `${PREFIX}-item`,
  spacer: `${PREFIX}-spacer`,
  footer: `${PREFIX}-footer`
}

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.root}`]: {
    overflow: 'hidden',
  },
  [`& .${classes.link}`]: {
    [theme.breakpoints.up('sm')]: {
      color: themeColor.sidebarText,
      ['&:hover']: {
        backgroundColor: themeColor.sidebarHover,
        color: themeColor.sidebarTextHover,
      },
    }
  },
  [`& .${classes.item}`]: {
    padding: '10px 12px',
    color: themeColor.sidebarText,
    fontSize: 14,
    ['&:hover']: {
      [theme.breakpoints.up('sm')]: {
        backgroundColor: themeColor.sidebarHover,
        color: themeColor.sidebarTextHover,
      }
    }
  },
  [`& .${classes.spacer}`]: {
    height: '64px',
  },
}))

const StyledMenuItemLink = styled(MenuItemLink)(() => ({
  'color': themeColor.sidebarText,
  'borderRadius': border.radius.sm,
  'transition': 'all 0.15s ease',
  '&.RaMenuItemLink-active': {
    backgroundColor: themeColor.sidebarActive,
    color: themeColor.sidebarActiveText,
    fontWeight: 600,
  },
  '& .MuiSvgIcon-root': {
    color: themeColor.sidebarText + ' !important',
    fontSize: '20px !important',
  },
  '& .MuiListItemIcon-root': {
    color: themeColor.sidebarText + ' !important',
    minWidth: '32px',
  },
  '&:hover .MuiSvgIcon-root': {
    color: themeColor.sidebarTextHover + ' !important',
  },
  '&:hover .MuiListItemIcon-root': {
    color: themeColor.sidebarTextHover + ' !important',
  },
  '&.RaMenuItemLink-active .MuiSvgIcon-root': {
    color: themeColor.sidebarActiveText + ' !important',
  },
  '&.RaMenuItemLink-active .MuiListItemIcon-root': {
    color: themeColor.sidebarActiveText + ' !important',
  },
}))

export default Menu
